import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useBookmarkPageContent } from '../../contexts/BookmarkPageContext';

/** Minimum width (px) for the bookmark list column; matches .bookmark-list-page-list-area in CSS */
export const BOOKMARK_LIST_MIN_WIDTH = 480;
/** Viewport width (px) at which the reader column / 3-column layout appears; matches --breakpoint-reader-column in CSS */
export const READER_COLUMN_BREAKPOINT_PX = 1400;
const MAX_WIDTH_PERCENT = 0.6; // Maximum list width as percentage of viewport

/** Style that collapses the list area (used for full layout and 3col when reader sidebar is open) */
const COLLAPSED_LIST_STYLE: CSSProperties = {
  maxWidth: 0,
  minWidth: 0,
  width: 0,
  flexBasis: 0,
  flexGrow: 0,
  flexShrink: 1,
  overflow: 'hidden',
  opacity: 0,
  visibility: 'hidden',
  borderWidth: 0,
};

const READER_COLUMN_QUERY = `(min-width: ${READER_COLUMN_BREAKPOINT_PX}px)`;

/** Hook that returns true when viewport is at or above the reader column breakpoint (1400px) */
export function useIsAboveReaderBreakpoint(): boolean {
  const [isAboveBreakpoint, setIsAboveBreakpoint] = useState(
    () =>
      typeof window !== 'undefined' && window.innerWidth >= READER_COLUMN_BREAKPOINT_PX,
  );

  useEffect(() => {
    const mq = window.matchMedia?.(READER_COLUMN_QUERY);
    if (!mq) {
      return;
    }

    const handler = () => setIsAboveBreakpoint(mq.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }

    if (typeof mq.addListener === 'function') {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
  }, []);

  return isAboveBreakpoint;
}

/** Hook that returns inline styles for the bookmark list area from effective layout mode. */
export function useListAreaStyle(): CSSProperties | undefined {
  const pageContent = useBookmarkPageContent();
  const { bookmarkListColumnWidth, showReaderSidebar } = useUserPreferences();

  if (!pageContent) return undefined;

  const { readerLayout: mode } = pageContent;

  const effectiveWidth =
    bookmarkListColumnWidth !== null && bookmarkListColumnWidth !== undefined
      ? bookmarkListColumnWidth
      : BOOKMARK_LIST_MIN_WIDTH;

  switch (mode) {
    case 'full':
      return COLLAPSED_LIST_STYLE;
    case '3col':
      if (showReaderSidebar) return COLLAPSED_LIST_STYLE;
      return {
        maxWidth: `${effectiveWidth}px`,
        flexBasis: `${effectiveWidth}px`,
        flexShrink: 0,
        flexGrow: 0,
        minWidth: `${BOOKMARK_LIST_MIN_WIDTH}px`,
      };
    case '2col':
    default:
      return {
        width: '100%',
        maxWidth: '100%',
        flexBasis: '100%',
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0,
      };
  }
}

export { MAX_WIDTH_PERCENT };
